<script setup lang="ts">
import CollectionCatalogListColumns from '~/components/collections/CollectionCatalogListColumns.vue';
import type { Collection } from '~/utilities/pyscript-api-models';
import CollectionCatalogListItem from '~/components/collections/CollectionCatalogListItem.vue';
import { useSortPreference } from '~/composables/sort-preference';
import IconChevronDown from '~icons/carbon/chevron-down';

defineProps<{
  collectionList: Collection[];
}>();

defineEmits<{
  (e: 'collectionDeleted', value: Collection): void;
  (e: 'collectionUpdated', value: Collection): void;
}>();

const { updateSort, savedSort } = useSortPreference();
</script>

<template>
  <TransitionGroup
    tag="section"
    name="collections-list-slide"
    class="relative rounded border border-new-gray-100/70 bg-white dark:border-new-gray-950/60 dark:bg-gray-675"
  >
    <div
      class="hidden border-b border-new-gray-50 py-2 text-sm font-medium dark:border-new-gray-950/60 sm:block"
    >
      <CollectionCatalogListColumns>
        <template #title>
          <button
            class="flex w-full items-center gap-2 hover:underline"
            @click="
              updateSort({
                sortBy: 'name',
                sortOrder: savedSort.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            "
          >
            <span>Title</span>
            <IconChevronDown
              class="shrink-0 text-13"
              :class="[
                savedSort.sortBy === 'name' && savedSort.sortOrder === 'asc' ? 'rotate-180' : '',
                savedSort.sortBy === 'name' ? '' : 'opacity-0',
              ]"
            />
          </button>
        </template>

        <template #description>
          <span class="cursor-default">Description</span>
        </template>

        <template #count>
          <span class="cursor-default">Count</span>
        </template>

        <template #modified>
          <button
            class="flex w-full items-center justify-end gap-2 hover:underline"
            @click="
              updateSort({
                sortBy: 'updated_at',
                sortOrder: savedSort.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            "
          >
            <span class="whitespace-nowrap">Last Modified</span>
            <IconChevronDown
              class="-mr-5 shrink-0 text-13"
              :class="[
                savedSort.sortBy === 'updated_at' && savedSort.sortOrder === 'asc'
                  ? 'rotate-180'
                  : '',
                savedSort.sortBy === 'updated_at' ? '' : 'opacity-0',
              ]"
            />
          </button>
        </template>
      </CollectionCatalogListColumns>
    </div>

    <CollectionCatalogListItem
      v-for="(collection, index) of collectionList"
      :key="collection.id"
      :collection="collection"
      :last-item="index === collectionList.length - 1"
      @collection-deleted="$emit('collectionDeleted', $event)"
      @collection-updated="$emit('collectionUpdated', $event)"
    />
  </TransitionGroup>
</template>

<style lang="postcss" scoped>
/* Only add animations if the user doesn't prefer reduced motion. */
@media (prefers-reduced-motion: no-preference) {
  .collections-list-slide {
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: scaleY(0.01) translate(30px, 0);
    }

    &-leave-active {
      position: absolute;
    }
  }
}
</style>
